import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import './BarChart.scss';
import { EChartsOption, SeriesOption } from 'echarts';
import { normalizeKeyword, SELECTED_BRAND_COLOR } from '../../../utils/utils';
import { getChartThemeColors, getBarsPalette } from '../../../utils/chartTheme';
import { useTheme } from '../../../contexts/theme/ThemeContext';
import { Skeleton } from 'primereact/skeleton';
import { TagsResponse } from '../../../models/tags/TagsResponse';
import { KeywordResponse } from '../../../models/monitor/KeywordResponse';
import { findTagIdInKeyword } from '../../../helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../helpers/tags/TagsHelper';
import { Tooltip } from 'primereact/tooltip';

// Grubość słupka w układzie poziomym (telefon); z niej liczona jest też wysokość wykresu.
const HORIZONTAL_BAR_WIDTH = 24;
// Przerwa między grupami słupków (kategoriami, np. chatbotami) w układzie poziomym.
const HORIZONTAL_GROUP_GAP = 40;

interface BarChartProps {
  data?: string[][] | null;
  colorPalette?: string[];
  colorsByName?: { name: string; color: string }[];
  keywordsMapper?: Map<string, string>;
  tags?: TagsResponse[];
  commonKeywords?: KeywordResponse[];
  onLegendClick?: (legendStateMap: Map<string, boolean>) => void;
  chartSize?: string | null;
  selectedBrand?: string;
  // Optional unit appended to values on the y-axis, bar labels, and tooltip (e.g. '%').
  valueSuffix?: string;
  // Category (x-axis) labels that get an info-icon marker (overlaid HTML) with a tooltip. Keyed by the
  // raw category label; value is the tooltip text. Used to flag archival models on the SoV-by-chatbot chart.
  axisLabelMarkers?: Record<string, string>;
  /**
   * Opt-in: render a custom icon (e.g. a brand logo/initial badge) instead of the plain colored
   * dot in each legend item. When omitted, the default `bar-chart__legend-color` dot is used.
   */
  renderLegendIcon?: (label: string, color: string) => React.ReactNode;
  /** Opt-in: capitalize brand names in the legend and the tooltip (display only). */
  capitalizeBrands?: boolean;
  /**
   * Opt-in (telefon): ten sam wykres obrócony — kategorie w pionie, serie jako poziome słupki obok
   * siebie, na tej samej skali co pionowo. Wąski ekran mieści wtedy wszystkie kategorie bez
   * przewijania w poziomie. Bez znaczników `axisLabelMarkers` — ich pozycje liczone są dla osi poziomej.
   */
  horizontal?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  colorPalette = getBarsPalette(),
  colorsByName,
  keywordsMapper,
  tags,
  commonKeywords,
  onLegendClick,
  chartSize,
  selectedBrand,
  valueSuffix,
  axisLabelMarkers,
  renderLegendIcon,
  capitalizeBrands = false,
  horizontal = false,
}) => {
  const [chartWidth, setChartWidth] = useState(0);
  const eChartsRef = useRef<ReactECharts>(null);
  const { theme } = useTheme();
  const [legendStateMap, setLegendStateMap] = useState<Map<string, boolean>>(new Map<string, boolean>());
  // Archival-model marker icons overlaid on the x-axis labels. The "?" tooltip lives ONLY on the icon
  // (an HTML element with our PrimeReact Tooltip), not on the whole ECharts axis label. Positions are
  // derived from each category's pixel x via convertToPixel.
  const [markerIcons, setMarkerIcons] = useState<
    { id: string; left: number; top: number; tooltip: string }[]
  >([]);

  useEffect(() => {
    if (horizontal || !axisLabelMarkers || !data || !data[0] || chartWidth <= 0) {
      setMarkerIcons([]);
      return;
    }
    // Defer so ECharts has applied its option / built the coordinate system before convertToPixel
    // (otherwise its ecModel is undefined and it throws). try/catch guards any remaining race.
    const timer = setTimeout(() => {
      const instance = eChartsRef.current?.getEchartsInstance();
      if (!instance || instance.isDisposed()) return;
      try {
        const baselineY = instance.convertToPixel({ yAxisIndex: 0 }, 0);
        const axisBaseline = typeof baselineY === 'number' ? baselineY : 0;
        // Measure label width to place the icon right AFTER the (centre-anchored) model name, at the
        // label's vertical level. Font mirrors the xAxis axisLabel (Inter, 10/12px by chart width).
        const fontSize = chartWidth < 700 ? 10 : 12;
        const AXIS_LABEL_MARGIN = 26; // mirrors xAxis.axisLabel.margin
        const ICON_SIZE = 14; // ico--xxs
        // Vertically centre the icon on the label text (label sits `margin` below the axis line).
        const iconTop = axisBaseline + AXIS_LABEL_MARGIN + fontSize / 2 - ICON_SIZE / 2;
        const measureCtx = document.createElement('canvas').getContext('2d');
        if (measureCtx) measureCtx.font = `${fontSize}px Inter, sans-serif`;
        const icons = data
          .slice(1)
          .map((row) => row[0])
          .filter((label) => !!axisLabelMarkers[label])
          .map((label) => {
            const centerX = instance.convertToPixel({ xAxisIndex: 0 }, label) as number;
            const textWidth = measureCtx ? measureCtx.measureText(label).width : label.length * fontSize * 0.6;
            return {
              // Slug the label into a valid DOM id: model names with spaces (e.g. "AI Mode") would
              // produce `#sov-hist-AI Mode`, an invalid CSS selector, so the Tooltip never attaches.
              id: `sov-hist-${label.replace(/[^a-zA-Z0-9_-]/g, '-')}`,
              left: centerX + textWidth / 2 + 10,
              top: iconTop,
              tooltip: axisLabelMarkers[label],
            };
          })
          .filter((m) => typeof m.left === 'number' && !isNaN(m.left));
        setMarkerIcons(icons);
      } catch {
        setMarkerIcons([]);
      }
    }, 60);

    return () => clearTimeout(timer);
  }, [horizontal, axisLabelMarkers, data, chartWidth, theme]);

  useEffect(() => {
    if (onLegendClick) {
      onLegendClick(legendStateMap);
    }
  }, [legendStateMap, onLegendClick]);

  const extendedColorsByName = useMemo(() => {
    if (!data || !data[0]) {
      return colorsByName || [];
    }

    const keywords = data[0].slice(1);
    const base = [...(colorsByName || [])];

    keywords.forEach((keyword, index) => {
      const normalizedKeyword = keyword.trim().toLowerCase();
      const exists = base.some((c) => normalizeKeyword(c.name).toLowerCase() === normalizedKeyword);

      if (!exists) {
        base.push({
          name: keyword,
          color: colorPalette[index % colorPalette.length],
        });
      }
    });

    return base;
  }, [data, colorsByName]);

  const seriesMeta = useMemo(() => {
    const keywords = data?.[0]?.slice(1) || [];
    return keywords.map((keyword, index) => {
      const tagId = commonKeywords ? findTagIdInKeyword(keyword, commonKeywords) : null;
      const keywordFromTags = tags ? getTagLabel(tags, tagId, keyword) : keyword;
      const displayLabel = keywordsMapper?.get(keywordFromTags) || keywordFromTags;
      const mappedByDisplay = extendedColorsByName.find(
        (c) => normalizeKeyword(c.name).toLowerCase() === displayLabel.trim().toLowerCase()
      )?.color;
      const mappedByOriginal = extendedColorsByName.find(
        (c) => normalizeKeyword(c.name).toLowerCase() === keyword.trim().toLowerCase()
      )?.color;
      const color = mappedByDisplay || mappedByOriginal || colorPalette[index % colorPalette.length];
      return { original: keyword, displayLabel, color } as {
        original: string;
        displayLabel: string;
        color: string;
      };
    });
  }, [data, tags, commonKeywords, keywordsMapper, extendedColorsByName]);

  useEffect(() => {
    if (seriesMeta.length > 0) {
      setLegendStateMap(new Map<string, boolean>(seriesMeta.map((m) => [m.displayLabel, true])));
    }
  }, [seriesMeta]);

  const chartPalette = useMemo(() => {
    if (seriesMeta.length === 0) return colorPalette;

    const basePalette = seriesMeta.map((m) => m.color);

    if (selectedBrand) {
      const index = seriesMeta.findIndex(
        (m) =>
          m.displayLabel.trim().toLowerCase() === selectedBrand.trim().toLowerCase() ||
          m.original.trim().toLowerCase() === selectedBrand.trim().toLowerCase()
      );

      if (index !== -1) {
        const updatedPalette = [...basePalette];
        updatedPalette[index] = SELECTED_BRAND_COLOR;
        return updatedPalette;
      }
    }

    return basePalette;
  }, [seriesMeta, selectedBrand]);

  const getSeries = (): SeriesOption[] => {
    const isToSmall = chartWidth < 700;

    return (
      seriesMeta.map((m) => ({
        name: m.displayLabel,
        type: 'bar',
        barWidth: horizontal ? HORIZONTAL_BAR_WIDTH : isToSmall ? 12 : 15,
        barGap: horizontal ? '25%' : isToSmall ? '50%' : '30%',
        itemStyle: {
          borderRadius: horizontal ? [0, 2, 2, 0] : [2, 2, 0, 0],
        },
        label: {
          fontFamily: 'Inter,sans-serif',
          // Poziomo (telefon) wartość stoi zawsze przy końcu słupka — nie ma najechania kursorem.
          show: horizontal,
          position: horizontal ? 'right' : 'top',
          color: chartTheme.tooltipText,
          fontSize: 8,
          fontWeight: 'bold',
          // In dataset mode `{c}` resolves to the whole row, not the bar value — pull the bar's
          // own value from the encoded y-dimension before appending the suffix.
          ...(valueSuffix
            ? {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formatter: (p: any) => {
                  const dim = p?.encode?.[horizontal ? 'x' : 'y']?.[0] ?? p.seriesIndex + 1;
                  const v = Array.isArray(p?.value) ? p.value[dim] : p?.value;
                  return v === undefined || v === null || v === '' ? '' : `${v}${valueSuffix}`;
                },
              }
            : {}),
        },
        emphasis: {
          label: {
            show: true,
          },
        },
      })) || []
    );
  };

  const yAxisMax = useMemo(() => {
    if (!data || data.length <= 1) {
      return 45;
    }

    const allValues: number[] = [];

    for (let i = 1; i < data.length; i++) {
      for (let j = 1; j < data[i].length; j++) {
        const value = parseFloat(data[i][j]);
        if (!isNaN(value)) {
          allValues.push(value);
        }
      }
    }

    if (allValues.length === 0) return 45;

    const maxValue = Math.max(...allValues);

    return maxValue + 5 < 45 ? maxValue + 5 : 45;
  }, [data]);

  const chartTheme = getChartThemeColors();

  const eChartsOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'none' },
      appendToBody: true,
      ...(capitalizeBrands ? { className: 'capitalize' } : {}),
      confine: false,
      borderColor: chartTheme.tooltipBorder,
      backgroundColor: chartTheme.tooltipBg,
      textStyle: {
        color: chartTheme.tooltipText,
      },
      ...(valueSuffix
        ? { valueFormatter: (value: unknown) => `${value ?? ''}${valueSuffix}` }
        : {}),
    },
    legend: {
      show: false,
      selected: Object.fromEntries(
        seriesMeta.map((m) => [m.displayLabel, legendStateMap.get(m.displayLabel) !== false])
      ),
    },
    grid: horizontal
      ? { top: 8, bottom: 24, left: 0, right: 8, containLabel: true }
      : {
          top: '50px',
          bottom: 40,
          left: '0',
          right: '0',
        },
    [horizontal ? 'yAxis' : 'xAxis']: {
      type: 'category',
      // Pierwsza kategoria na górze, jak w kolejności kolumn na desktopie.
      inverse: horizontal,
      axisLabel: {
        fontFamily: 'Inter,sans-serif',
        fontSize: chartWidth < 700 ? '10px' : '12px',
        fontWeight: 'normal',
        color: chartTheme.axisLabel,
        margin: horizontal ? 8 : 26,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    [horizontal ? 'xAxis' : 'yAxis']: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: chartTheme.splitLine } },
      axisLabel: {
        color: chartTheme.axisLabel,
        ...(valueSuffix ? { formatter: `{value}${valueSuffix}` } : {}),
      },
      max: yAxisMax,
    },
    dataset: {
      source: data || [],
    },
    color: chartPalette,
    series: getSeries(),
  };

  useEffect(() => {
    if (!eChartsRef.current) {
      return;
    }

    const chartInstance = eChartsRef.current.getEchartsInstance();
    const container = chartInstance.getDom();

    let resizeTimeout: NodeJS.Timeout | null = null;

    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
          if (!eChartsRef.current) return;

          const instance = eChartsRef.current.getEchartsInstance();
          if (!instance || instance.isDisposed()) return;

          const currentContainer = instance.getDom();
          if (!currentContainer) return;

          const newWidth = currentContainer.clientWidth;
          if (newWidth !== chartWidth && newWidth > 0) {
            setChartWidth(newWidth);
            try {
              instance.dispatchAction({
                type: 'hideTip',
              });

              instance.resize();
              instance.setOption(eChartsOptions, true);
            } catch (e) {
              console.warn('ECharts resize error:', e);
            }
          }
        });
      }, 100);
    });

    resizeObserver.observe(container);

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeObserver.disconnect();
    };
  }, [eChartsOptions, chartWidth]);

  // Poziomo wysokość rośnie z liczbą kategorii i serii: każda kategoria to grupa słupków jeden pod
  // drugim (słupek + odstęp na serię — `barGap` 25% słupka — plus przerwa między grupami).
  const categoryCount = Math.max(1, (data?.length ?? 1) - 1);
  const chartSizeStyle = {
    height: horizontal
      ? `${categoryCount * (seriesMeta.length * (HORIZONTAL_BAR_WIDTH + 6) + HORIZONTAL_GROUP_GAP) + 32}px`
      : '235px',
    width: '100%',
  };

  const handleLegendItemClick = (label: string) => {
    setLegendStateMap((prevState) => {
      const updatedMap = new Map(prevState);
      const currentVisibility = updatedMap.has(label) ? !updatedMap.get(label) : false;
      updatedMap.set(label, currentVisibility);

      const canManage =
        Array.from(updatedMap.values()).filter((value) => !value).length + 1 <= updatedMap.size;

      if (!canManage && prevState.get(label) === true) {
        return prevState;
      }

      if (eChartsRef.current) {
        const instance = eChartsRef.current.getEchartsInstance();
        instance.dispatchAction({
          type: 'legendToggleSelect',
          name: label,
        });
      }

      return updatedMap;
    });
  };

  if (!data) {
    return (
      <div className="bar-chart">
        <div className="bar-chart__legend">
          <Skeleton width="200px" height="20px" />
        </div>
        <div className="bar-chart__box">
          <Skeleton width="100%" height="300px" />
        </div>
      </div>
    );
  }

  const renderLegendItem = (index: number, meta: { displayLabel: string; color: string }) => {
    const isSelected = legendStateMap.get(meta.displayLabel);

    return (
      <div
        key={index}
        className={`bar-chart__legend-item ${isSelected ? 'bar-chart__legend-item--active' : ''}`}
        onClick={() => handleLegendItemClick(meta.displayLabel)}
      >
        <span>
          {renderLegendIcon ? (
            renderLegendIcon(meta.displayLabel, meta.color)
          ) : (
            <span className="bar-chart__legend-color" style={{ background: meta.color }} />
          )}
          {meta.displayLabel}
        </span>
      </div>
    );
  };
  return (
    <div
      className={`bar-chart ${chartSize ? 'bar-chart--sm' : ''}${horizontal ? ' bar-chart--horizontal' : ''}`}
    >
      <div className={`bar-chart__legend${capitalizeBrands ? ' capitalize' : ''}`}>
        {seriesMeta.map((m, index) => renderLegendItem(index, m))}
      </div>
      <div className="bar-chart__box">
        <ReactECharts
          key={theme}
          ref={eChartsRef}
          option={eChartsOptions}
          notMerge={true}
          style={chartSizeStyle}
        />
        {markerIcons.map((marker) => (
          <React.Fragment key={marker.id}>
            <span
              id={marker.id}
              className="ico--info ico--xxs bar-chart__axis-marker"
              style={{ left: `${marker.left}px`, top: `${marker.top}px` }}
            />
            <Tooltip
              target={`#${marker.id}`}
              content={marker.tooltip}
              className="tooltip--top"
              position="top"
              showDelay={5}
              style={{ width: '240px' }}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default BarChart;
